import { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

// Telechargement d'une photo base64 : le lien <a download> avec data-URI est ignore
// par Safari/PWA iOS. On passe par la feuille de partage (Enregistrer l'image) sur
// mobile, avec repli blob+download sur desktop.
async function savePhoto(dataUri, fileName) {
  try {
    const blob = await (await fetch(dataUri)).blob();
    const file = new File([blob], fileName, { type: blob.type || 'image/jpeg' });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file] });
      return;
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  } catch (e) {
    if (e && e.name === 'AbortError') return; // partage annule par l'utilisateur
    window.open(dataUri, '_blank'); // dernier recours : ouvrir l'image
  }
}
import { toast } from 'sonner';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ClipboardList, Plus, Trash2, Camera, Package, ShoppingCart, Check, X, Eye, Archive, ChevronDown, ChevronUp, RefreshCw, Play, RotateCcw, Clock, Truck, Lock, Pencil, Download } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

import { markModuleSeen } from '@/lib/moduleBadges';

// Les trois stocks permanents passent devant tous les chantiers dans les listes
// de choix (demande Pavel 31/08/2026), dans cet ordre : Depot, Sprinter, Master.
// Les chantiers suivent, par ordre alphabetique.
const STOCK_ORDER = ['Stock Dépôt', 'Stock Sprinter', 'Stock Master'];

const sortStocksFirst = (projects) =>
  [...projects].sort((a, b) => {
    if (a.is_stock !== b.is_stock) return a.is_stock ? -1 : 1;
    if (a.is_stock) {
      const ia = STOCK_ORDER.indexOf(a.name);
      const ib = STOCK_ORDER.indexOf(b.name);
      // Un stock inconnu de la liste passe apres les trois connus.
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    }
    return (a.name || '').localeCompare(b.name || '', 'fr');
  });

export default function MaterialsPage() {
  // Ouvrir le module efface sa pastille de notifications
  useEffect(() => { markModuleSeen(['material']); }, []);
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const location = useLocation();
  const isManager = user?.role === 'manager';
  const [materials, setMaterials] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [selectedProject, setSelectedProject] = useState('all');
  const [showPhotoPreview, setShowPhotoPreview] = useState(null);

  // La liste ne porte plus que des vignettes de quelques kilooctets (13,3 Mo
  // avant, environ 45 s sur une 4G faible : la page restait vide sur un chantier
  // mal couvert). Les photos pleine taille sont chargees a l'ouverture.
  const ouvrirPhotos = async (material) => {
    setShowPhotoPreview({ ...material, chargement: true });
    try {
      const { data } = await axios.get(`${API}/materials/${material.id}/photos`);
      setShowPhotoPreview({ ...material, ...data, chargement: false });
    } catch (error) {
      console.error('Failed to fetch material photos:', error);
      setShowPhotoPreview({ ...material, chargement: false });
    }
  };
  const [showArchives, setShowArchives] = useState(false);
  const [showDisabled, setShowDisabled] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null);
  const [filterProjects, setFilterProjects] = useState([]);
  // Noms des stocks permanents (Depot, Sprinter, Master) : sert a coller
  // l'icone 📦 devant le nom dans la liste du materiel.
  const stockNames = useMemo(
    () => new Set(filterProjects.filter(p => p.is_stock).map(p => p.name)),
    [filterProjects]
  );
  const [editingQuantityId, setEditingQuantityId] = useState(null);
  const [editingQuantityValue, setEditingQuantityValue] = useState('');
  const [editingNameId, setEditingNameId] = useState(null);
  const [editingNameValue, setEditingNameValue] = useState('');
  const [form, setForm] = useState({
    project_id: '',
    name: '',
    quantity: 1,
    unit: 'pièce',
    photos: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitLockRef = useRef(false);
  const fileInputRef = useRef(null);

  // Helper function for translations
  const getText = (fr, ru, md, uk) => {
    if (language === 'ru') return ru;
    if (language === 'md') return md;
    if (language === 'uk') return uk || fr; // Fallback to French if no Ukrainian
    return fr;
  };

  const units = [
    { value: 'pièce', label: 'Pièce' },
    { value: 'paquet', label: 'Paquet' },
    { value: 'boite', label: 'Boîte' },
    { value: 'rouleau', label: 'Rouleau' },
    { value: 'kg', label: 'Kg' },
    { value: 'ml', label: 'ML (mètre linéaire)' },
  ];

  const fetchData = async () => {
    try {
      const [materialsRes, projectsRes] = await Promise.all([
        axios.get(`${API}/materials`),
        axios.get(`${API}/projects/light`)
      ]);
      
      // Show all materials including stock
      setMaterials(materialsRes.data);
      
      // Tous les projets, stocks compris (Depot, Sprinter, Master).
      // Les trois stocks remontent EN TETE de toute liste de choix du chantier
      // (demande Pavel 31/08/2026) : ce sont les destinations les plus
      // frequentes, on ne doit pas avoir a faire defiler les chantiers pour
      // les atteindre. Ordre impose entre eux : Depot, Sprinter, Master.
      const allProjects = sortStocksFirst(projectsRes.data || []);
      setProjects(allProjects);
      setFilterProjects(allProjects);
      
      // Stock du depot propose par defaut a la saisie
      const stockProject = allProjects.find(p => p.is_stock && p.name === 'Stock Dépôt')
        || allProjects.find(p => p.is_stock);
      if (stockProject && !form.project_id) {
        setForm(prev => ({ ...prev, project_id: stockProject.id }));
      } else if (allProjects.length > 0 && !form.project_id) {
        setForm(prev => ({ ...prev, project_id: allProjects[0].id }));
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Compress image to reduce size and latency - Keep quality readable for references
  const compressImage = (file, maxWidth = 1200, quality = 0.85) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Resize if too large
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to compressed JPEG
          const compressedData = canvas.toDataURL('image/jpeg', quality);
          resolve(compressedData);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle navigation from ProjectsPage shortcut
  useEffect(() => {
    if (location.state?.addFor && projects.length > 0) {
      setForm(prev => ({ ...prev, project_id: location.state.addFor }));
      setShowAdd(true);
      // Clear state so it doesn't reopen on refresh
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, projects]);

  const handleUpdateQuantity = async (materialId) => {
    if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
    const qty = parseInt(editingQuantityValue);
    if (!qty || qty < 1) {
      toast.error(getText('Quantité invalide', 'Неверное количество', 'Cantitate invalidă', 'Невірна кількість'));
      return;
    }
    try {
      const response = await axios.patch(`${API}/materials/${materialId}/quantity`, { quantity: qty });
      setMaterials(materials.map(m => m.id === materialId ? response.data : m));
      setEditingQuantityId(null);
      toast.success(getText('Quantité mise à jour', 'Количество обновлено', 'Cantitate actualizată', 'Кількість оновлено'));
    } catch (error) {
      toast.error(getText('Erreur', 'Ошибка', 'Eroare', 'Помилка'));
    }
  };

  // Renommage d'un materiel deja valide (demande Pavel 31/08/2026) : un libelle
  // saisi a la va-vite sur un chantier doit pouvoir etre corrige apres coup,
  // sans avoir a supprimer la ligne et la ressaisir. Manager uniquement.
  const handleUpdateName = async (materialId) => {
    if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
    const libelle = (editingNameValue || '').trim();
    if (!libelle) {
      toast.error(getText('Le nom ne peut pas être vide', 'Название не может быть пустым', 'Numele nu poate fi gol', 'Назва не може бути порожньою'));
      return;
    }
    try {
      const response = await axios.patch(`${API}/materials/${materialId}/name`, { name: libelle });
      setMaterials(materials.map(m => m.id === materialId ? response.data : m));
      setEditingNameId(null);
      toast.success(getText('Nom mis à jour', 'Название обновлено', 'Nume actualizat', 'Назву оновлено'));
    } catch (error) {
      toast.error(error.response?.data?.detail || getText('Erreur', 'Ошибка', 'Eroare', 'Помилка'));
    }
  };

  // Affichage du nom d'un materiel, avec le crayon de renommage pour le manager.
  // Utilise dans les trois listes (a commander / commande, recurrents, valides)
  // pour que le renommage soit possible partout, pas seulement dans les archives.
  const renderMaterialName = (material, textClass) => {
    if (editingNameId === material.id) {
      return (
        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
          <input
            type="text"
            autoFocus
            value={editingNameValue}
            onChange={e => setEditingNameValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') handleUpdateName(material.id);
              if (e.key === 'Escape') setEditingNameId(null);
            }}
            className="flex-1 min-w-0 text-sm border rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
            style={{ fontSize: '16px' }}
          />
          <button onClick={() => handleUpdateName(material.id)} className="p-1.5 text-green-600 bg-green-50 hover:bg-green-100 rounded">
            <Check className="w-4 h-4" />
          </button>
          <button onClick={() => setEditingNameId(null)} className="p-1.5 text-slate-400 hover:bg-slate-100 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>
      );
    }
    return <p className={textClass}>{material.name}</p>;
  };

  // Bouton de renommage, place dans la rangee d'icones a cote du recurrent /
  // recommander et de la corbeille (demande Pavel 31/08/2026).
  const renderRenameButton = (material) => {
    if (!isManager) return null;
    return (
      <button
        onClick={(e) => { e.stopPropagation(); setEditingNameId(material.id); setEditingNameValue(material.name); }}
        className="p-1.5 text-slate-400 hover:text-blue-500 hover:bg-blue-50 rounded transition-colors"
        title={getText('Renommer', 'Переименовать', 'Redenumește', 'Перейменувати')}
        data-testid={`rename-material-${material.id}`}
      >
        <Pencil className="w-4 h-4" />
      </button>
    );
  };

  const handlePhotoSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    
    for (const file of files) {
      if (!file.type.startsWith('image/')) continue;
      try {
        const compressedData = await compressImage(file);
        setForm(prev => ({ ...prev, photos: [...prev.photos, compressedData] }));
      } catch {}
    }
    
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleAdd = async () => {
    // Verrou synchrone anti double-tap : sur mobile deux taps rapprochés
    // partaient avant le re-render React, creant le materiau deux fois
    // (doublons Sergei du 28/08, 4 paires de POST a 1 s d'intervalle).
    if (submitLockRef.current) return;
    submitLockRef.current = true;

    if (!form.project_id || !form.name.trim()) {
      toast.error(getText('Veuillez remplir les champs obligatoires', 'Заполните обязательные поля', 'Completați câmpurile obligatorii', 'Заповніть обов\'язкові поля'));
      submitLockRef.current = false;
      return;
    }

    setIsSubmitting(true);
    
    try {
      const payload = {
        project_id: form.project_id,
        name: form.name.trim(),
        quantity: form.quantity,
        unit: form.unit
      };
      
      // Only add photos if they exist
      if (form.photos.length > 0) {
        payload.photo_data = form.photos[0]; // First photo as main
        payload.photos = form.photos; // All photos
      }
      
      const response = await axios.post(`${API}/materials`, payload);
      setMaterials([response.data, ...materials]);
      setShowAdd(false);
      setForm(prev => ({ ...prev, name: '', quantity: 1, unit: 'pièce', photos: [] }));
      toast.success(getText('Matériau ajouté', 'Материал добавлен', 'Material adăugat', 'Матеріал додано'));
    } catch (error) {
      console.error('Error adding material:', error);
      toast.error(getText('Erreur lors de l\'ajout', 'Ошибка добавления', 'Eroare la adăugare', 'Помилка додавання'));
    } finally {
      setIsSubmitting(false);
      submitLockRef.current = false;
    }
  };

  const handleValidate = async (materialId) => {
    // Find the material to show appropriate confirmation message
    const material = materials.find(m => m.id === materialId);
    const isArchiving = material?.status === 'ordered';
    
    const confirmMsg = isArchiving 
      ? getText(
          'Voulez-vous vraiment archiver ce matériau ?',
          'Вы уверены, что хотите архивировать этот материал?',
          'Sigur doriți să arhivați acest material?',
          'Ви впевнені, що хочете архівувати цей матеріал?'
        )
      : getText(
          'Voulez-vous vraiment valider cette commande ?',
          'Вы уверены, что хотите подтвердить этот заказ?',
          'Sigur doriți să validați această comandă?',
          'Ви впевнені, що хочете підтвердити це замовлення?'
        );
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      const response = await axios.put(`${API}/materials/${materialId}/validate`);
      setMaterials(materials.map(m => m.id === materialId ? response.data : m));
      
      const newStatus = response.data.status;
      if (newStatus === 'ordered') {
        toast.success(getText('Commande passée !', 'Заказ размещен!', 'Comandă plasată!', 'Замовлення розміщено!'));
      } else if (newStatus === 'archived') {
        toast.success(getText('Matériau archivé', 'Материал архивирован', 'Material arhivat', 'Матеріал архівовано'));
      }
    } catch (error) {
      toast.error(getText('Erreur', 'Ошибка', 'Eroare', 'Помилка'));
    }
  };

  const handleDelete = async (materialId) => {
    const confirmMsg = getText(
      'Voulez-vous vraiment supprimer ce matériau ?',
      'Вы уверены, что хотите удалить этот материал?',
      'Sigur doriți să ștergeți acest material?',
      'Ви впевнені, що хочете видалити цей матеріал?'
    );
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      await axios.delete(`${API}/materials/${materialId}`);
      setMaterials(materials.filter(m => m.id !== materialId));
      toast.success(getText('Supprimé', 'Удалено', 'Șters', 'Видалено'));
    } catch (error) {
      toast.error(getText('Erreur', 'Ошибка', 'Eroare', 'Помилка'));
    }
  };

  const handleToggleDisabled = async (materialId) => {
    try {
      const response = await axios.put(`${API}/materials/${materialId}/toggle-disabled`);
      // Add the new recurring material to the list
      setMaterials([...materials, response.data]);
      toast.success(getText('Commande créée dans les récurrentes !', 'Заказ создан в повторяющихся!', 'Comandă creată în recurente!', 'Замовлення створено в повторюваних!'));
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(getText('Ce matériau existe déjà dans les récurrents', 'Этот материал уже существует в повторяющихся', 'Acest material există deja în recurente', 'Цей матеріал вже існує в повторюваних'));
      } else {
        toast.error(getText('Erreur', 'Ошибка', 'Eroare', 'Помилка'));
      }
    }
  };

  const handleCreateFromRecurring = async (materialId) => {
    try {
      const response = await axios.post(`${API}/materials/${materialId}/create-from-recurring`);
      setMaterials([...materials, response.data]);
      toast.success(getText('Nouveau matériau créé dans À commander !', 'Новый материал создан в К заказу!', 'Material nou creat în De comandat!', 'Новий матеріал створено в До замовлення!'));
    } catch (error) {
      toast.error(getText('Erreur', 'Ошибка', 'Eroare', 'Помилка'));
    }
  };

  const handleReorder = async (materialId) => {
    try {
      const response = await axios.put(`${API}/materials/${materialId}/reorder`);
      setMaterials(materials.map(m => m.id === materialId ? response.data : m));
      toast.success(getText('Matériau remis à commander !', 'Материал переупорядочен!', 'Material recomandat!', 'Матеріал знову до замовлення!'));
    } catch (error) {
      toast.error(getText('Erreur', 'Ошибка', 'Eroare', 'Помилка'));
    }
  };

  // Filter materials by project and separate active vs archived vs disabled
  const projectFiltered = selectedProject === 'all' 
    ? materials 
    : materials.filter(m => m.project_id === selectedProject);
  
  const activeMaterials = projectFiltered.filter(m => m.status !== 'archived' && !m.is_disabled);
  const disabledMaterials = projectFiltered.filter(m => m.is_disabled && m.status !== 'archived');
  const archivedMaterials = projectFiltered.filter(m => m.status === 'archived');

  const pendingCount = activeMaterials.filter(m => m.status === 'pending').length;
  const orderedCount = activeMaterials.filter(m => m.status === 'ordered').length;
  const disabledCount = disabledMaterials.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="materials-page" className="space-y-6 animate-fadeIn">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="page-header">{t('materialsTitle')}</h1>
        <Button
          onClick={() => setShowAdd(true)}
          className="btn-primary flex items-center gap-2"
          data-testid="add-material-btn"
        >
          <Plus className="w-4 h-4" />
          {getText('Ajouter', 'Добавить', 'Adaugă', 'Додати')}
        </Button>
      </div>
      
      {/* Filter & Stats */}
      <div className="card-base p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Label className="form-label mb-2">{getText('Filtrer par chantier', 'Фильтр по проекту', 'Filtrare după proiect', 'Фільтр за об\'єктом')}</Label>
            <select value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
              <option value="all">{getText('Tous les chantiers', 'Все проекты', 'Toate proiectele', 'Всі об\'єкти')}</option>
              {filterProjects.map(p => (
                <option key={p.id} value={p.id}>
                  {p.is_stock ? `📦 ${p.name}` : p.name}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-4 gap-1 sm:flex sm:items-end sm:gap-4">
            <button 
              onClick={() => setStatusFilter(statusFilter === 'pending' ? null : 'pending')}
              className={`text-center p-1.5 sm:p-2 rounded-lg transition-colors ${statusFilter === 'pending' ? 'bg-orange-100 ring-2 ring-orange-400' : 'hover:bg-gray-100'}`}
            >
              <span className="text-lg sm:text-2xl font-bold text-orange-500">{pendingCount}</span>
              <p className="text-[8px] sm:text-xs text-slate-500 uppercase font-semibold leading-tight truncate">
                {getText('À commander', 'К заказу', 'De comandat', 'Замов.')}
              </p>
            </button>
            <button 
              onClick={() => setStatusFilter(statusFilter === 'ordered' ? null : 'ordered')}
              className={`text-center p-1.5 sm:p-2 rounded-lg transition-colors ${statusFilter === 'ordered' ? 'bg-emerald-100 ring-2 ring-emerald-400' : 'hover:bg-gray-100'}`}
            >
              <span className="text-lg sm:text-2xl font-bold text-emerald-500">{orderedCount}</span>
              <p className="text-[8px] sm:text-xs text-slate-500 uppercase font-semibold leading-tight truncate">
                {getText('Commandé', 'Заказано', 'Comandat', 'Замовл.')}
              </p>
            </button>
            <button 
              onClick={() => setStatusFilter(statusFilter === 'recurring' ? null : 'recurring')}
              className={`text-center p-1.5 sm:p-2 rounded-lg transition-colors ${statusFilter === 'recurring' ? 'bg-blue-100 ring-2 ring-blue-400' : 'hover:bg-gray-100'}`}
            >
              <span className="text-lg sm:text-2xl font-bold text-blue-500">{disabledCount}</span>
              <p className="text-[8px] sm:text-xs text-slate-500 uppercase font-semibold leading-tight truncate">
                {getText('Récurrent', 'Повтор.', 'Recurent', 'Повтор.')}
              </p>
            </button>
            <button 
              onClick={() => setStatusFilter(statusFilter === 'archived' ? null : 'archived')}
              className={`text-center p-1.5 sm:p-2 rounded-lg transition-colors ${statusFilter === 'archived' ? 'bg-slate-200 ring-2 ring-slate-400' : 'hover:bg-gray-100'}`}
            >
              <span className="text-lg sm:text-2xl font-bold text-slate-400">{archivedMaterials.length}</span>
              <p className="text-[8px] sm:text-xs text-slate-500 uppercase font-semibold leading-tight truncate">
                {getText('Archivé', 'Архив', 'Arhivat', 'Архів')}
              </p>
            </button>
          </div>
        </div>
      </div>
      
      {/* Add Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {getText('Ajouter un matériau', 'Добавить материал', 'Adaugă material', 'Додати матеріал')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="form-label">{getText('Chantier', 'Проект', 'Proiect', 'Об\'єкт')} *</Label>
              <select value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm">
                <option value="">{getText('Sélectionner', 'Выбрать', 'Selectează', 'Вибрати')}</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.is_stock ? `📦 ${p.name}` : p.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <Label className="form-label">{getText('Nom du matériau', 'Название материала', 'Nume material', 'Назва матеріалу')} *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ex: Sac de ciment 35kg"
                className="input-base"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="form-label">{getText('Quantité', 'Количество', 'Cantitate', 'Кількість')}</Label>
                <Input
                  type="number"
                  min="1"
                  value={form.quantity || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setForm({ ...form, quantity: val === '' ? '' : parseInt(val) || '' });
                  }}
                  onBlur={(e) => {
                    // Si vide ou invalide au blur, remettre 1
                    if (!form.quantity || form.quantity < 1) {
                      setForm({ ...form, quantity: 1 });
                    }
                  }}
                  className="input-base"
                />
              </div>
              <div>
                <Label className="form-label">{getText('Unité', 'Единица', 'Unitate', 'Одиниця')}</Label>
                <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                  <SelectTrigger className="input-base">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {units.map(u => (
                      <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Photo Section - Multiple photos */}
            <div>
              <Label className="form-label">{getText('Photos (pour commande)', 'Фото (для заказа)', 'Poze (pentru comandă)', 'Фото (для замовлення)')}</Label>
              <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handlePhotoSelect} className="hidden" />
              
              <div className="flex gap-2 mt-2 flex-wrap">
                {form.photos.map((p, i) => (
                  <div key={i} className="relative">
                    <img src={p} alt="" className="w-20 h-20 rounded-lg object-cover border" />
                    <button type="button" onClick={() => setForm(prev => ({...prev, photos: prev.photos.filter((_, idx) => idx !== i)}))} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <button type="button" onClick={() => fileInputRef.current?.click()} className="flex-1 py-2 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center gap-2 text-gray-500 hover:bg-gray-50 text-xs font-medium">
                  <Camera className="w-4 h-4" /> {getText('Ajouter photos', 'Добавить фото', 'Adaugă poze', 'Додати фото')}
                </button>
              </div>
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button onClick={() => setShowAdd(false)} variant="outline" className="flex-1">
                {getText('Annuler', 'Отмена', 'Anulează', 'Скасувати')}
              </Button>
              <Button onClick={handleAdd} disabled={isSubmitting} className="btn-primary flex-1">
                {isSubmitting
                  ? getText('Ajout...', 'Добавление...', 'Se adaugă...', 'Додавання...')
                  : getText('Créer', 'Создать', 'Creează', 'Створити')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Photo Preview Modal */}
      {showPhotoPreview && (
        <div className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-4" onClick={() => setShowPhotoPreview(null)}>
          <div className="relative bg-white rounded-2xl max-w-lg w-full overflow-hidden max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between shrink-0">
              <h3 className="font-semibold truncate pr-2">{showPhotoPreview.name}</h3>
              <button onClick={() => setShowPhotoPreview(null)} className="p-2 hover:bg-gray-100 rounded-full shrink-0">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3 p-3 overflow-y-auto">
              {showPhotoPreview.chargement && (
                <p className="py-8 text-center text-sm text-gray-500">
                  {getText('Chargement des photos...', 'Загрузка фотографий...', 'Se încarcă pozele...', 'Завантаження фото...')}
                </p>
              )}
              {(showPhotoPreview.photos?.length ? showPhotoPreview.photos : [showPhotoPreview.photo_data]).filter(Boolean).map((photo, i) => (
                <div key={i} className="space-y-2">
                  <img src={photo} alt={`${showPhotoPreview.name} ${i+1}`} className="w-full rounded-lg" />
                  <button type="button" onClick={() => savePhoto(photo, `${showPhotoPreview.name}-${i+1}.jpg`)}
                    className="w-full py-2.5 bg-gray-100 text-gray-800 rounded-lg text-sm font-medium hover:bg-gray-200 flex items-center justify-center gap-2">
                    <Download className="w-4 h-4" /> {getText('Télécharger', 'Скачать', 'Descarcă', 'Завантажити')}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {/* Active Materials List */}
      {activeMaterials.length === 0 && archivedMaterials.length === 0 && disabledMaterials.length === 0 ? (
        <div className="card-base p-12">
          <div className="text-center">
            <ClipboardList className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 mb-4">{getText('Aucun matériau', 'Нет материалов', 'Niciun material', 'Немає матеріалів')}</p>
            <Button onClick={() => setShowAdd(true)} className="btn-primary">
              {getText('Ajouter le premier matériau', 'Добавить первый материал', 'Adaugă primul material', 'Додати перший матеріал')}
            </Button>
          </div>
        </div>
      ) : (
        <>
          {/* Filter indicator */}
          {statusFilter && (
            <div className="flex items-center gap-2 mb-4">
              <span className="text-sm text-slate-500">
                {getText('Filtre actif:', 'Активный фильтр:', 'Filtru activ:', 'Активний фільтр:')}
              </span>
              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                statusFilter === 'pending' ? 'bg-orange-100 text-orange-700' :
                statusFilter === 'ordered' ? 'bg-emerald-100 text-emerald-700' :
                statusFilter === 'recurring' ? 'bg-blue-100 text-blue-700' :
                'bg-slate-100 text-slate-700'
              }`}>
                {statusFilter === 'pending' ? getText('À commander', 'К заказу', 'De comandat', 'До замовлення') :
                 statusFilter === 'ordered' ? getText('Commandé', 'Заказано', 'Comandat', 'Замовлено') :
                 statusFilter === 'recurring' ? getText('Récurrent', 'Повторяющийся', 'Recurent', 'Повторюваний') :
                 getText('Archivé', 'Архивировано', 'Arhivat', 'Архівовано')}
              </span>
              <button 
                onClick={() => setStatusFilter(null)}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          )}

          {/* Show based on filter */}
          {(!statusFilter || statusFilter === 'pending' || statusFilter === 'ordered') && (
            <>
              {/* Active Materials - Compact Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {activeMaterials
                  .filter(m => !statusFilter || m.status === statusFilter)
                  .map((material, index) => (
              <div
                key={material.id}
                className={`card-base p-3 animate-slideIn transition-all border-l-3 ${
                  material.status === 'ordered' 
                    ? 'border-l-emerald-500 bg-emerald-50/50' 
                    : 'border-l-orange-500 bg-orange-50/50'
                }`}
                style={{ animationDelay: `${index * 0.02}s` }}
              >
                <div className="flex items-center gap-3">
                  {/* Photo or Icon - Small */}
                  {(material.thumb_data || material.photos_count > 0) ? (
                    <button
                      onClick={() => ouvrirPhotos(material)}
                      className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 hover:opacity-80 transition-opacity border border-gray-200 relative"
                    >
                      <img src={material.thumb_data} alt={material.name} className="w-full h-full object-cover" />
                      {material.photos_count > 1 && <span className="absolute bottom-0 right-0 bg-black/60 text-white text-[8px] px-1 rounded-tl">{material.photos_count}</span>}
                    </button>
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <Package className="w-5 h-5 text-gray-400" />
                    </div>
                  )}
                  
                  {/* Content - Compact */}
                  <div className="flex-1 min-w-0">
                    {renderMaterialName(material, "font-semibold text-slate-900 text-sm break-words")}
                    <p className="text-xs text-slate-500">
                      {material.quantity} {material.unit} • {stockNames.has(material.project_name) ? `📦 ${material.project_name}` : material.project_name}
                    </p>
                    {material.created_by_name && (
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        {getText('Par', 'От', 'De la', 'Від')} {material.created_by_name}
                      </p>
                    )}
                  </div>
                  
                  {/* Status Badge - Compact */}
                  <div className={`px-2 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${
                    material.status === 'ordered' 
                      ? 'bg-emerald-100 text-emerald-700' 
                      : 'bg-orange-100 text-orange-700'
                  }`}>
                    {material.status === 'ordered' ? '✓' : '⏳'}
                  </div>
                </div>
                
                {/* Actions - Compact Row */}
                <div className="flex items-center mt-2 pt-2 border-t border-gray-100">
                  <div className="flex-1 flex justify-center gap-2">
                    {material.status === 'pending' ? (
                      /* Status: pending - Show "À commander" button (orange with clock) - Only manager can click */
                      <button
                        onClick={isManager ? () => handleValidate(material.id) : undefined}
                        disabled={!isManager}
                        className={`flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-full transition-all shadow-sm ${
                          isManager 
                            ? 'bg-orange-500 hover:bg-orange-600 text-white cursor-pointer' 
                            : 'bg-orange-200 text-orange-600 cursor-not-allowed'
                        }`}
                        title={!isManager ? getText('Seul le manager peut commander', 'Только менеджер может заказать', 'Numai managerul poate comanda', 'Тільки менеджер може замовити') : ''}
                      >
                        <span className={`w-5 h-5 rounded-full flex items-center justify-center ${isManager ? 'bg-white/30' : 'bg-orange-300/50'}`}>
                          {isManager ? <Clock className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                        </span>
                        {getText('À commander', 'Заказать', 'De comandat', 'До замовлення')}
                      </button>
                    ) : (
                      /* Status: ordered - Show grey "Commandé" label + green "Reçu" button - ALL employees can click */
                      <>
                        <span className="flex items-center gap-2 px-3 py-1.5 bg-gray-200 text-gray-600 text-xs font-semibold rounded-full">
                          <span className="w-5 h-5 rounded-full bg-gray-400/30 flex items-center justify-center">
                            <Check className="w-3 h-3" />
                          </span>
                          {getText('Commandé', 'Заказано', 'Comandat', 'Замовлено')}
                        </span>
                        <button
                          onClick={() => handleValidate(material.id)}
                          className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-full transition-all shadow-sm bg-emerald-500 hover:bg-emerald-600 text-white cursor-pointer"
                        >
                          <span className="w-5 h-5 rounded-full flex items-center justify-center bg-white/30">
                            <Truck className="w-3 h-3" />
                          </span>
                          {getText('Reçu', 'Получено', 'Primit', 'Отримано')}
                        </button>
                      </>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-1">
                    {renderRenameButton(material)}
                    <button
                      onClick={() => handleToggleDisabled(material.id)}
                      className="p-1.5 text-slate-400 hover:text-blue-500 hover:bg-blue-50 rounded transition-colors"
                      title="Récurrent"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </button>
                    
                    <button
                      onClick={() => handleDelete(material.id)}
                      className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
              </div>
            </>
          )}
          
          {/* Disabled/Recurring Section */}
          {(!statusFilter || statusFilter === 'recurring') && disabledMaterials.length > 0 && (
            <div className={statusFilter === 'recurring' ? '' : 'mt-6'}>
              {statusFilter !== 'recurring' && (
                <button
                  onClick={() => setShowDisabled(!showDisabled)}
                  className="w-full flex items-center justify-between p-4 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <RefreshCw className="w-5 h-5 text-blue-500" />
                    <span className="font-semibold text-blue-700">
                      {getText('Commandes récurrentes', 'Повторяющиеся заказы', 'Comenzi recurente', 'Повторювані замовлення')} ({disabledMaterials.length})
                    </span>
                  </div>
                  {showDisabled ? <ChevronUp className="w-5 h-5 text-blue-500" /> : <ChevronDown className="w-5 h-5 text-blue-500" />}
                </button>
              )}
              
              {(showDisabled || statusFilter === 'recurring') && (
                <div className={statusFilter === 'recurring' ? 'space-y-2' : 'mt-3 space-y-2'}>
                  {disabledMaterials.map((material) => (
                    <div
                      key={material.id}
                      className="card-base p-3 bg-blue-50/50 border-l-4 border-l-blue-400"
                    >
                      <div className="flex items-center gap-3">
                        {material.thumb_data ? (
                          <button
                            onClick={() => ouvrirPhotos(material)}
                            className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0"
                          >
                            <img src={material.thumb_data} alt={material.name} className="w-full h-full object-cover opacity-70" />
                          </button>
                        ) : (
                          <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <Package className="w-6 h-6 text-blue-400" />
                          </div>
                        )}
                        <div className="flex-1">
                          <p className="font-medium text-slate-700">{material.name}</p>
                          <p className="text-sm text-slate-500">{material.quantity} {material.unit} • {material.project_name}</p>
                        </div>
                        <button
                          onClick={isManager ? () => handleCreateFromRecurring(material.id) : undefined}
                          disabled={!isManager}
                          className={`flex items-center gap-2 px-3 py-2 text-sm font-semibold rounded-lg transition-colors ${
                            isManager 
                              ? 'bg-orange-500 hover:bg-orange-600 text-white cursor-pointer' 
                              : 'bg-orange-200 text-orange-600 cursor-not-allowed'
                          }`}
                          title={!isManager ? getText('Seul le manager peut placer', 'Только менеджер может разместить', 'Numai managerul poate plasa', 'Тільки менеджер може розмістити') : ''}
                        >
                          {isManager ? <Play className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                          {getText('Placer dans À commander', 'Поместить в К заказу', 'Plasați în De comandat', 'Помістити в До замовлення')}
                        </button>
                        <button
                          onClick={() => handleDelete(material.id)}
                          className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Archives Section */}
          {(!statusFilter || statusFilter === 'archived') && archivedMaterials.length > 0 && (
            <div className={statusFilter === 'archived' ? '' : 'mt-6'}>
              {statusFilter !== 'archived' && (
                <button
                  onClick={() => setShowArchives(!showArchives)}
                  className="w-full flex items-center justify-between p-4 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Archive className="w-5 h-5 text-slate-500" />
                    <span className="font-semibold text-slate-700">
                      {getText('Archives', 'Архивы', 'Arhive', 'Архіви')} ({archivedMaterials.length})
                    </span>
                  </div>
                  {showArchives ? <ChevronUp className="w-5 h-5 text-slate-500" /> : <ChevronDown className="w-5 h-5 text-slate-500" />}
                </button>
              )}
              
              {(showArchives || statusFilter === 'archived') && (
                <div className={statusFilter === 'archived' ? 'space-y-2' : 'mt-3 space-y-2'}>
                  {archivedMaterials.map((material) => (
                    <div
                      key={material.id}
                      className="card-base p-3 bg-slate-50 border-l-4 border-l-slate-300"
                    >
                      <div className="flex items-center gap-3">
                        {material.thumb_data ? (
                          <button
                            onClick={() => ouvrirPhotos(material)}
                            className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0"
                          >
                            <img src={material.thumb_data} alt={material.name} className="w-full h-full object-cover opacity-70" />
                          </button>
                        ) : (
                          <div className="w-12 h-12 rounded-lg bg-slate-200 flex items-center justify-center flex-shrink-0">
                            <Package className="w-6 h-6 text-slate-400" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          {renderMaterialName(material, "font-medium text-slate-600 break-words")}
                          {editingQuantityId === material.id ? (
                            <div className="flex items-center gap-1 mt-1" onClick={e => e.stopPropagation()}>
                              <input
                                type="number"
                                min="1"
                                autoFocus
                                value={editingQuantityValue}
                                onChange={e => setEditingQuantityValue(e.target.value)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') handleUpdateQuantity(material.id);
                                  if (e.key === 'Escape') setEditingQuantityId(null);
                                }}
                                className="w-20 text-sm border rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-400"
                                style={{ fontSize: '16px' }}
                              />
                              <span className="text-xs text-slate-500">{material.unit}</span>
                              <button onClick={() => handleUpdateQuantity(material.id)} className="p-1 text-green-600 hover:bg-green-50 rounded">
                                <Check className="w-3.5 h-3.5" />
                              </button>
                              <button onClick={() => setEditingQuantityId(null)} className="p-1 text-slate-400 hover:bg-slate-100 rounded">
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1">
                              <p className="text-sm text-slate-400">{material.quantity} {material.unit} • {material.project_name}</p>
                              <button
                                onClick={() => { setEditingQuantityId(material.id); setEditingQuantityValue(material.quantity); }}
                                className="p-0.5 text-slate-300 hover:text-blue-500 transition-colors"
                                title={getText('Modifier la quantité', 'Изменить количество', 'Modifică cantitatea', 'Змінити кількість')}
                              >
                                <Pencil className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {renderRenameButton(material)}
                          <button
                            onClick={() => handleReorder(material.id)}
                            className="flex items-center gap-2 px-3 py-2 text-sm font-semibold rounded-lg transition-colors bg-orange-500 hover:bg-orange-600 text-white cursor-pointer"
                            data-testid={`reorder-material-${material.id}`}
                          >
                            <RotateCcw className="w-4 h-4" />
                            {getText('Recommander', 'Перезаказать', 'Recomandă', 'Перезамовити')}
                          </button>
                          <button
                            onClick={() => handleDelete(material.id)}
                            className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
